import React, { useEffect, useState } from 'react'
import VisitDetail from './VisitDetail'
import SkeletonCardLoading from '../../../shared/SkeltonCardLoading'
import NoDataFound from '../../../shared/NoDataFound'
import {
  getVisitsList,
  setVisitFilters,
  setVisitFiltersPayload
} from '../../../redux/slices/VisitsSlice'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import AppointmentDetailCard from './AppointmentDetailCard'
import EmptyVisitCard from './EmptyVisitCard'
import { getVisitColor } from '../../../utils/getStatusBackgroundClass'
import BreakCardDesign from './BreakCardDesign'
import MixedAppointmentVisitCard from './MixedAppointmentVisitCard'
import MultipleVisitsCard from './MultipleVisitsCard'
import { convert24To12 } from '../../../utils/convert24To12'
import { useLocation } from 'react-router-dom'
import {
  FaFemale,
  FaMale,
  FaQuestion,
  FaSearch,
  FaSortAmountDown,
  FaSortAmountDownAlt,
  FaIdCard,
  FaHashtag,
  FaFile
} from 'react-icons/fa'
import { CalculateAge } from '../../../utils/CalculateAge'
import Flag from 'react-world-flags'
import { getCountryCode } from '../../../utils/CountryCodes'

function VisitsList ({
  visitsData,
  isLoading,
  filtersPayload,
  practitionerDepartmentList,
  visit_category,
  priority,
  setFiltersPayload,
  status,
  order,
  orderData,
  getOrdersPayload,
  containerRef,
  isNursingStation,
  is_temporary_followup
}) {
  
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [searchDocumentId, setSearchDocumentId] = useState('')
  const [searchTokenNumber, setSearchTokenNumber] = useState('')
  const [searchFileNo, setSearchFileNo] = useState('')
  const [activeSearch, setActiveSearch] = useState('document')
  const { practitionersList, visitCategoryFilter, visitStatusFilter } =
    useSelector(state => state?.visits)
  const prevVisitFilters = useSelector(state => state?.visits?.visitFilters)
  const isNurse = useSelector(
    state => state.auth.user?.role?.name_en == 'Nurse'
  )
  const isPractitioner = useSelector(
    state => state.auth.user?.role?.name_en == 'Practitioner'
  )
  const isRcmAssistant = useSelector(
    state => state.auth.user?.role?.name_en == 'Rcm Assistant'
  )

  const [sortOrder, setSortOrder] = useState(isPractitioner ? 'desc' : '')

  const useVisitOwnCategory =
    priority === 'EMERGENCY' || !!is_temporary_followup

  useEffect(() => {
    if (!isRcmAssistant) {
      const payload = {
        ...filtersPayload,
        visit_category,
        priority: priority || '',
        payment_status: '',
        status: filtersPayload?.status || 'All',
        start_date: moment(filtersPayload?.start_date).format('YYYY-MM-DD'),
        end_date: moment(filtersPayload?.start_date).format('YYYY-MM-DD')
      }
      const requestPayload = is_temporary_followup
        ? { ...payload, is_temporary_followup }
        : payload
      if (!isNurse && payload?.practitioner_id) {
        dispatch(getVisitsList(requestPayload))
      }

      if (isNurse && payload?.nurse_id) {
        dispatch(getVisitsList(requestPayload))
      }
      setFiltersPayload(payload)
      dispatch(setVisitFiltersPayload(payload))
    }
  }, [filtersPayload?.practitioner_id, priority, is_temporary_followup])

  useEffect(() => {
    if (isRcmAssistant) {
      const payload = {
        patient_id: '',
        practitioner_id: '',
        visit_no: null,
        order_no: '',
        nurse_id: filtersPayload?.nurse_id,
        status: 'All',
        start_date: moment(prevVisitFilters?.start_date).format('YYYY-MM-DD'),
        end_date: moment(prevVisitFilters?.start_date).format('YYYY-MM-DD'),
        invoice_no: null,
        company_invoice_no: null,
        visit_category: visitCategoryFilter,
        payment_status: visitStatusFilter,
        page: 1,
        perPage: 10
      }
      dispatch(getVisitsList(payload))
      setFiltersPayload(payload)
      dispatch(setVisitFiltersPayload(payload))
    }
  }, [])

  useEffect(() => {
    const date = new Date()
    const currentMinutes = date.getHours() * 60 + date.getMinutes()

    const getMinutesFromTime = time => {
      const [hours, minutes] = time.split(':').map(Number)
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
      return hours * 60 + minutes
    }

    const scrollTimer = setTimeout(() => {
      const container = containerRef?.current
      const elements = container?.querySelectorAll('[data-visit-time]')

      if (!container || !elements?.length) return
      let nearestElement = null
      let nearestDifference = Infinity

      for (let element of elements) {
        const visit24 = element.dataset.visitTime
        if (!visit24) continue

        const visitMinutes = getMinutesFromTime(visit24)
        if (visitMinutes === null) continue

        const difference = Math.abs(visitMinutes - currentMinutes)
        if (
          difference < nearestDifference ||
          (difference === nearestDifference && visitMinutes >= currentMinutes)
        ) {
          nearestDifference = difference
          nearestElement = element
        }
      }

      if (!nearestElement) return

      const containerTop = container.scrollTop
      const containerRect = container.getBoundingClientRect()
      const elementRect = nearestElement.getBoundingClientRect()

      const offset =
        elementRect.top -
        containerRect.top +
        containerTop -
        container.clientHeight / 2 +
        nearestElement.clientHeight / 2

      container.scrollTo({
        top: offset,
        behavior: 'smooth'
      })
    }, 0)

    return () => clearTimeout(scrollTimer)
  }, [visitsData])

  const switchSearchMode = mode => {
    setActiveSearch(mode)
  }

  const getTokenNumber = slot => {
    if (slot?.visit?.token?.token_number) {
      return parseInt(slot?.visit?.token?.token_number, 10) || 0
    }
    return 0
  }

  const toggleSortOrder = () => {
    if (sortOrder === '') setSortOrder('desc')
    else if (sortOrder === 'desc') setSortOrder('asc')
    else setSortOrder('')
  }

  const getFilteredAndSortedSlots = slots => {
    if (!slots || !Array.isArray(slots)) return []

    let filteredSlots = [...slots]

    if (priority === 'EMERGENCY') {
      filteredSlots = filteredSlots.filter(slot => {
        const slotVisits = Array.isArray(slot?.visits)
          ? slot.visits
          : [slot?.visit]

        return slotVisits.some(visit => {
          const visitPriority = visit?.priority?.coding?.[0]

          return (
            visitPriority?.code?.toLowerCase() === 'em' ||
            visitPriority?.display?.toUpperCase() === 'EMERGENCY'
          )
        })
      })
    }

    if (is_temporary_followup) {
      filteredSlots = filteredSlots.filter(slot => {
        const slotVisits = Array.isArray(slot?.visits)
          ? slot.visits
          : [slot?.visit]

        return slotVisits.some(visit => visit?.is_temporary_followup === true)
      })
    }

    if (activeSearch === 'document' && searchDocumentId.trim() !== '') {
      filteredSlots = filteredSlots.filter(slot => {
        const lowerCaseSearchTerm = searchDocumentId.toLowerCase()
        const documentId =
          slot?.visit?.patient?.document_id?.toString()?.toLowerCase() || ''
        return documentId.includes(lowerCaseSearchTerm)
      })
    } else if (activeSearch === 'token' && searchTokenNumber.trim() !== '') {
      filteredSlots = filteredSlots.filter(slot => {
        const lowerCaseSearchTerm = searchTokenNumber.toLowerCase()
        const tokenNumber =
          slot?.visit?.token?.token_number?.toString()?.toLowerCase() || ''
        return tokenNumber.includes(lowerCaseSearchTerm)
      })
    } else if (activeSearch === 'file' && searchFileNo.trim() !== '') {
      filteredSlots = filteredSlots.filter(slot => {
        const search = searchFileNo.trim().toLowerCase()
        const fileNo =
          slot?.visit?.patient?.file_no?.toString().toLowerCase() || ''
        return fileNo.includes(search)
      })
    }

    if (sortOrder === 'asc') {
      filteredSlots.sort((a, b) => getTokenNumber(a) - getTokenNumber(b))
    } else if (sortOrder === 'desc') {
      filteredSlots.sort((a, b) => getTokenNumber(b) - getTokenNumber(a))
    }

    return filteredSlots
  }

  const handleDocumentIdChange = e => {
    setSearchDocumentId(e.target.value)
    if (e.target.value) {
      setActiveSearch('document')
      setSearchTokenNumber('')
      setSearchFileNo('')
    }
  }

  const handleTokenNumberChange = e => {
    setSearchTokenNumber(e.target.value)
    if (e.target.value) {
      setActiveSearch('token')
      setSearchDocumentId('')
      setSearchFileNo('')
    }
  }
  const handleFileNumberChange = e => {
    setSearchFileNo(e.target.value)
    if (e.target.value) {
      setActiveSearch('file')
      setSearchDocumentId('')
      setSearchTokenNumber('')
    }
  }

  const clearSearch = () => {
    if (activeSearch === 'document') {
      setSearchDocumentId('')
    } else if (activeSearch === 'token') {
      setSearchTokenNumber('')
    } else {
      setSearchFileNo('')
    }
  }

  const getSortButtonTitle = () => {
    if (sortOrder === 'asc') return t('Sort by token number (ascending)')
    if (sortOrder === 'desc') return t('Sort by token number (descending)')
    return t('Sort by token number')
  }

  const getActiveSearchTerm = () => {
    return activeSearch === 'document' ? searchDocumentId : searchTokenNumber
  }

  return (
    <div>
      <div className='sticky top-0 z-20 bg-white '>
        <div className='py-2 px-3'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex flex-wrap gap-2'>
              <button
                onClick={() => switchSearchMode('document')}
                className={`flex items-center px-3 py-2 text-sm transition-all ${
                  activeSearch === 'document'
                    ? 'border-b-2 border-iconC text-iconC font-medium'
                    : 'text-gray-500 hover:text-iconC'
                }`}
              >
                <FaIdCard className='mr-1' /> {t('Document ID')}
              </button>
              <button
                onClick={() => switchSearchMode('token')}
                className={`flex items-center px-3 py-2 text-sm transition-all ${
                  activeSearch === 'token'
                    ? 'border-b-2 border-iconC text-iconC font-medium'
                    : 'text-gray-500 hover:text-iconC'
                }`}
              >
                <FaHashtag className='mr-1' /> {t('Token Number')}
              </button>
              <button
                onClick={() => switchSearchMode('file')}
                className={`flex items-center px-3 py-2 text-sm transition-all ${
                  activeSearch === 'file'
                    ? 'border-b-2 border-iconC text-iconC font-medium'
                    : 'text-gray-500 hover:text-iconC'
                }`}
              >
                <FaFile className='mr-1 ' /> {t('MRN')}
              </button>
            </div>

            <div className='flex flex-grow items-center gap-2 w-full md:w-auto order-last md:order-none'>
              <div className='flex-grow flex items-center bg-white border border-gray-300 rounded-lg px-3 py-2 focus-within:border-iconC transition-colors'>
                <FaSearch className='text-iconC mr-2' />
                {activeSearch === 'document' ? (
                  <input
                    type='text'
                    value={searchDocumentId}
                    onChange={handleDocumentIdChange}
                    placeholder={t('Search by document ID')}
                    className='w-full focus:outline-none text-gray-700 text-sm'
                  />
                ) : activeSearch === 'file' ? (
                  <input
                    type='text'
                    value={searchFileNo}
                    onChange={handleFileNumberChange}
                    placeholder={t('Search by MRN')}
                    className='w-full focus:outline-none text-gray-700 text-sm'
                  />
                ) : (
                  <input
                    type='text'
                    value={searchTokenNumber}
                    onChange={handleTokenNumberChange}
                    placeholder={t('Search by token number')}
                    className='w-full focus:outline-none text-gray-700 text-sm'
                  />
                )}
                {(activeSearch === 'document' && searchDocumentId) ||
                (activeSearch === 'token' && searchTokenNumber) ||
                (activeSearch === 'file' && searchFileNo) ? (
                  <button
                    onClick={clearSearch}
                    className='text-gray-400 hover:text-gray-600 transition-colors ml-2'
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>

            <div className='flex items-center ml-auto md:ml-0'>
              <button
                onClick={toggleSortOrder}
                className={`flex items-center justify-center p-2 rounded-lg transition-all ${
                  sortOrder
                    ? 'bg-iconC text-white '
                    : 'bg-white border border-iconC text-iconC hover:bg-iconC hover:text-white'
                }`}
                title={getSortButtonTitle()}
              >
                {sortOrder === 'asc' ? (
                  <FaSortAmountDownAlt size={18} />
                ) : (
                  <FaSortAmountDown size={18} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {visitsData?.slots_data?.days?.[0] &&
      visitsData?.slots_data?.days?.[0]?.slots?.length &&
      !isLoading ? (
        <div className='mt-2'>
          <div className='grid grid-cols-1 gap-y-2 px-3'>
            {getFilteredAndSortedSlots(
              isRcmAssistant
                ? visitsData?.slots_data?.days?.[0]?.slots?.filter(
                    data => data?.visit?.visit_payment_type == 'insurance'
                    // &&
                    //   data?.visit?.items?.some(
                    //     (item) => item?.item_category == "insurance"
                    //   )
                  )
                : visitsData?.slots_data?.days?.[0]?.slots
            )?.map((data, index) => {
              const isVisit = data?.booking_type === 'visit'
              const isAppointment = data?.booking_type === 'appointment'
              const isMixed = data?.booking_type === 'mixed'
              const isBreak = data?.booking_type === 'break'
              const isNurseVisits = data?.booking_type === 'nurse_visits'
              const isBooked = data?.is_booked
              const isStatusMatch = data?.visit?.payment_status === status
              let SlotComponent = (
                <EmptyVisitCard
                  payment_status={status}
                  key={index}
                  data={data}
                  filtersPayload={filtersPayload}
                  visit_category={visit_category}
                  practitionerDepartmentList={practitionerDepartmentList}
                  order={order}
                  orderData={orderData}
                  getOrdersPayload={getOrdersPayload}
                />
              )

              if (isPractitioner && !isBooked) {
                return null
              }

              if (isNurseVisits && Array.isArray(data?.visits)) {
                SlotComponent = (
                  <MultipleVisitsCard
                    getOrdersPayload={getOrdersPayload}
                    order={order}
                    orderData={orderData}
                    visitColor={
                      data?.visit?.status.toLowerCase() === 'cancelled' ||
                      data?.appointment?.status.toLowerCase() === 'cancelled'
                        ? 'bg-red-200'
                        : getVisitColor(data?.booking_type)
                    }
                    key={index}
                    visitsData={
                      is_temporary_followup
                        ? data.visits.filter(
                            visit => visit?.is_temporary_followup === true
                          )
                        : priority === 'EMERGENCY'
                          ? data.visits.filter(visit => {
                              const visitPriority = visit?.priority?.coding?.[0]

                              return (
                                visitPriority?.code?.toLowerCase() === 'em' ||
                                visitPriority?.display?.toUpperCase() ===
                                  'EMERGENCY'
                              )
                            })
                          : data.visits
                    }
                    getVisitDataPayload={filtersPayload}
                    slotData={data}
                    visitCategory={
                      useVisitOwnCategory
                        ? data?.visit?.visit_category
                        : visit_category
                    }
                    useVisitCategoryFromVisit={useVisitOwnCategory}
                  />
                )
              } else if (isVisit && (isBooked || data?.visit)) {
                SlotComponent = (
                  <VisitDetail
                    getOrdersPayload={getOrdersPayload}
                    order={order}
                    orderData={orderData}
                    key={index}
                    visitColor={
                      data?.visit?.status.toLowerCase() === 'cancelled' ||
                      data?.appointment?.status.toLowerCase() === 'cancelled'
                        ? 'bg-red-200'
                        : getVisitColor(data?.booking_type)
                    }
                    visitData={data?.visit}
                    getVisitDataPayload={filtersPayload}
                    slotData={data}
                    visitCategory={
                      useVisitOwnCategory
                        ? data?.visit?.visit_category
                        : visit_category
                    }
                  />
                )
              } else if (isAppointment) {
                SlotComponent = (
                  <AppointmentDetailCard
                    order={order}
                    key={index}
                    visitColor={
                      data?.visit?.status.toLowerCase() === 'cancelled' ||
                      data?.appointment?.status.toLowerCase() === 'cancelled'
                        ? 'bg-red-200'
                        : getVisitColor(data?.booking_type)
                    }
                    appointmentData={data?.appointment}
                    getVisitDataPayload={filtersPayload}
                    orderData={orderData}
                    getOrdersPayload={getOrdersPayload}
                    practitionerDepartmentList={practitionerDepartmentList}
                  />
                )
              } else if (isMixed && (isBooked || data?.visit)) {
                SlotComponent = (
                  <MixedAppointmentVisitCard
                    getOrdersPayload={getOrdersPayload}
                    order={order}
                    orderData={orderData}
                    key={index}
                    slotData={data}
                    visitColor={
                      data?.visit?.status.toLowerCase() === 'cancelled' ||
                      data?.appointment?.status.toLowerCase() === 'cancelled'
                        ? 'bg-red-200'
                        : getVisitColor(data?.booking_type)
                    }
                    visitCategory={
                      useVisitOwnCategory
                        ? data?.visit?.visit_category
                        : visit_category
                    }
                    mixedData={data?.visit}
                    getVisitDataPayload={filtersPayload}
                    practitionerDepartmentList={practitionerDepartmentList}
                    isNursingStation={isNursingStation}
                    containerRef={containerRef}
                  />
                )
              } else if (isBreak) {
                SlotComponent = (
                  <BreakCardDesign
                    key={index}
                    data={data}
                    filtersPayload={filtersPayload}
                    practitionerDepartmentList={practitionerDepartmentList}
                  />
                )
              }
              return (
                <div
                  key={index}
                  data-visit-time={data?.time}
                  className='slot-row flex min-w-0 items-stretch overflow-hidden rounded-md border border-gray-200 bg-transparent'
                >
                  {!isRcmAssistant && (
                    <div
                      className={`min-w-[70px] ${
                        i18n.language === 'ar' ? 'border-l' : 'border-r'
                      } border-gray-200 h-full flex items-center flex-col justify-between  `}
                    >
                      <div
                        className={`bg-gray-100 font-semibold border-none w-full flex items-center justify-center h-[50px] ${
                          data?.visit?.status.toLowerCase() === 'cancelled' ||
                          data?.appointment?.status.toLowerCase() ===
                            'cancelled'
                            ? '!bg-red-200'
                            : data?.visit?.priority?.coding?.[0]?.code?.toLowerCase() ===
                                'em' ||
                              data?.visit?.priority?.coding?.[0]?.display?.toUpperCase() ===
                                'EMERGENCY'
                            ? '!bg-orange-200'
                            : getVisitColor(data?.booking_type)
                        } ${isVisit ? 'lg:h-[84px] xl:h-[60px]' : 'h-[50px]'}`}
                      >
                        {convert24To12(data?.time, i18n.language)}
                      </div>
                      <div className='flex min-h-0 flex-1 items-center flex-col justify-evenly'>
                        <div>
                          {(data?.visit?.patient?.nationality ||
                            data?.appointment?.patient?.nationality) && (
                            <Flag
                              code={getCountryCode(
                                data?.visit?.patient?.nationality ||
                                  data?.appointment?.patient?.nationality ||
                                  'Saudi'
                              )}
                              fallback={<span>{t('Unknown')}</span>}
                              className='h-6 w-6 xl:h-8 xl:w-8'
                            />
                          )}
                        </div>
                        {data?.visit?.token?.token_number && (
                          <div>
                            <h2 className='text-[12px] !font-semibold  !text-center !uppercase !mt-1 !tracking-wider'>
                              {t('TOKEN')}
                            </h2>

                            <p className='text-[20px] text-center w-full !font-semibold'>
                              {data?.appointment_id ||
                              data?.appointment?.appointment_id ||
                              data?.visit?.appointment_id
                                ? `A${data?.visit?.token?.token_number ||
                                    data?.visit?.token_number ||
                                    1}`
                                : data?.visit?.token?.token_number ||
                                  data?.visit?.token_number ||
                                  1}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className='min-w-0 flex-1'>{SlotComponent}</div>
                </div>
              )
            })}
          </div>

          {getFilteredAndSortedSlots(
            isRcmAssistant
              ? visitsData?.slots_data?.days?.[0]?.slots?.filter(
                  data =>
                    data?.visit?.visit_payment_type == 'insurance' &&
                    data?.visit?.items?.some(
                      item => item?.item_category == 'insurance'
                    )
                )
              : visitsData?.slots_data?.days?.[0]?.slots
          )?.length === 0 &&
            getActiveSearchTerm() && (
              <div className='mt-4 text-center'>
                <p className='text-gray-600'>
                  {activeSearch === 'document'
                    ? t('No visits found with document ID containing')
                    : activeSearch === 'file'
                    ? t('No visits found with file number containing')
                    : t('No visits found with token number containing')}
                  "{getActiveSearchTerm()}"
                </p>
              </div>
            )}
        </div>
      ) : isLoading ? (
        <div className='grid grid-cols-1 w-full gap-y-2'>
          {[...Array(4)].map((_, index) => (
            <SkeletonCardLoading
              key={index}
              count={4}
              parentClassName='w-full  bg-white rounded-md py-4'
            />
          ))}
        </div>
      ) : (
        <NoDataFound
          title={t('No Slots found.')}
          iconSize='40'
          titleClassName='text-20-dark font-semibold'
        />
      )}
    </div>
  )
}

export default VisitsList
